import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import useApi from '@/hooks/useApi';
import useEmailFieldValidation from '@/hooks/useEmailFieldValidation';
import useApp from '@/pages/App.context';
import FormInput from '@/components/FormInput';
import FormError from '@/components/FormError';
import Button from '@/components/Button';
import styles from '@/pages/Auth/Auth.module.scss';

type ForgottenPasswordInputs = {
  email: string;
};

const ForgottenPassword = () => {
  const { t } = useTranslation(['auth']);
  const { setAppTitle } = useApp();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [forgotPassword] = useApi('users.password.forgot');

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<ForgottenPasswordInputs>();

  const { emailRegistration, handleEmailBlur, handleEmailChange } =
    useEmailFieldValidation<ForgottenPasswordInputs>({
      fieldName: 'email',
      register,
      requiredMessage: t('auth:fields.emailRequired'),
      patternMessage: t('auth:fields.emailValidity'),
      setValue,
      trigger,
    });

  // submit form handler
  const doForgotPassword: SubmitHandler<ForgottenPasswordInputs> = async (
    data
  ) => {
    setLoading(true);
    await forgotPassword({
      email: data.email,
    });
    // TODO: Actually do request to BE once available.
    void navigate('/auth/forgotten-password/result');
  };

  useEffect(() => {
    setAppTitle(t('auth:forgotten.title'));
  }, [setAppTitle, t]);

  return (
    <>
      <h2 className={styles.heading}>{t('auth:forgotten.title')}</h2>
      <div className={styles.text}>{t('auth:forgotten.desc')}</div>
      <form onSubmit={handleSubmit(doForgotPassword)} className={styles.form}>
        <FormInput
          autoComplete="email"
          errorMessage={errors.email?.message}
          id="email"
          label={t('auth:fields.email')}
          placeholder={t('auth:fields.emailPlaceholder')}
          type="email"
          {...emailRegistration}
          onBlur={handleEmailBlur}
          onChange={handleEmailChange}
        />
        {errors.root?.message && (
          <FormError isRoot message={errors.root.message} />
        )}
        <Button className={styles.button} submit loading={loading}>
          {t('auth:forgotten.submit')}
        </Button>
        <Button
          as="link"
          className={classNames(styles.button, styles.back)}
          variant="tertiary"
          to="/auth/login"
        >
          {t('auth:back')}
        </Button>
      </form>
    </>
  );
};

export default ForgottenPassword;
